import React, { useState, useEffect } from 'react';
import { usePropertyFilters } from '../../contexts/PropertyFilterContext';
import { FileText, MapPin, User, Building } from 'lucide-react';
import { formatValue } from '../../utils/formatters/valueUtils';
import { formatDateBr } from '../../utils/formatters/dateUtils';
import DataItem from './ui/DataItem';
import RecordNavigator from './ui/RecordNavigator';

/**
 * Componente para visualização de dados do Cadastro de Imóveis Rurais (CAFIR)
 * de uma propriedade rural, seguindo padrões visuais e utilizando utilitários compartilhados.
 */
const CafirTab = ({ propertyData, onHighlightGeometry }) => {
  // Estado local para armazenar detalhes do CAFIR selecionado
  const [selectedCafirDetail, setSelectedCafirDetail] = useState(null);

  // Estado para controle de navegação entre registros
  const [currentIndex, setCurrentIndex] = useState(0);

  // Acesso aos códigos CAFIR selecionados do contexto
  const { selectedCafirCodes, setSelectedCafirCodes } = usePropertyFilters();

  // Log inicial para debug (padrão em todos os tabs)
  // eslint-disable-next-line no-console
  console.log('[CafirTab] Recebendo dados:', {
    temDados: propertyData ? 'Sim' : 'Não',
    temProperties: propertyData?.properties ? 'Sim' : 'Não',
    temCafir: propertyData?.properties?.cafir ? 'Sim' : 'Não',
    codigosSelecionados: selectedCafirCodes,
  });

  // Validação de dados de entrada
  if (!propertyData?.properties?.cafir) {
    return (
      <div className="p-4 bg-gray-50 rounded border border-gray-200">
        <div className="flex items-center text-gray-500">
          <FileText className="mr-2" size={18} />
          <p>Nenhum dado CAFIR disponível para esta propriedade.</p>
        </div>
      </div>
    );
  }

  // Normalização para garantir compatibilidade com diferentes formatos de dados
  const cafirData = propertyData.properties.cafir;

  // Detectar o formato dos dados (podem vir como array de detalhes ou como objeto direto)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  let cafirDetails = [];

  // Se temos um array de detalhes, usamos ele
  if (Array.isArray(cafirData)) {
    cafirDetails = cafirData;
  }
  // Se temos um array dentro do objeto details
  else if (cafirData.details && Array.isArray(cafirData.details)) {
    cafirDetails = cafirData.details;
  }
  // Se não há array, mas temos nirf, tratamos o próprio cafirData como um detalhe
  else if (cafirData.nirf) {
    cafirDetails = [cafirData];
  }
  // Se não temos nirf explicitamente, mas temos outros campos que possam identificar um NIRF
  else {
    // Verificamos se o objeto tem algum campo que indica que é um detalhe válido
    const indicativeFields = ['codigo', 'codigo_cafir', 'codigo_nirf', 'NIRF', 'nirf'];
    const hasValidField = indicativeFields.some((field) => cafirData[field]);

    if (hasValidField || typeof cafirData === 'object') {
      cafirDetails = [cafirData];
    }
  }

  // Função para navegar entre registros CAFIR
  const handleNavigate = (newIndex) => {
    if (newIndex >= 0 && newIndex < cafirDetails.length) {
      setCurrentIndex(newIndex);
      const selectedDetail = cafirDetails[newIndex];
      setSelectedCafirDetail(selectedDetail);

      // Atualizar o contexto com o código NIRF do registro selecionado
      const nirf =
        selectedDetail.nirf ||
        selectedDetail.NIRF ||
        selectedDetail.codigo_nirf ||
        selectedDetail.codigo_cafir ||
        selectedDetail.codigo ||
        selectedDetail.codigo_incra;

      if (nirf) {
        setSelectedCafirCodes([nirf]);
      }
    }
  };

  // Efeito para selecionar o detalhe CAFIR com base no código selecionado ou índice atual
  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {
    // Se não temos detalhes CAFIR disponíveis, não faz nada
    if (!cafirDetails.length) return;

    // Se há um código CAFIR selecionado, tenta encontrar o registro correspondente
    if (selectedCafirCodes?.length > 0) {
      // Primeiro tenta encontrar uma correspondência exata no campo nirf
      let selected = cafirDetails.find(
        (detail) => detail.nirf === selectedCafirCodes[0] || detail.NIRF === selectedCafirCodes[0],
      );

      // Se não encontrou, tenta outros campos comuns
      if (!selected) {
        const possibleFields = ['codigo', 'codigo_cafir', 'codigo_nirf', 'code', 'codigo_incra'];

        for (const field of possibleFields) {
          selected = cafirDetails.find((detail) => detail[field] === selectedCafirCodes[0]);

          if (selected) {
            // Atualizar o índice para corresponder ao registro encontrado
            const foundIndex = cafirDetails.indexOf(selected);
            if (foundIndex !== -1) {
              setCurrentIndex(foundIndex);
            }
            break;
          }
        }
      }

      // Se encontrou um registro correspondente ao NIRF selecionado, usa ele
      if (selected) {
        setSelectedCafirDetail(selected);
        return;
      } else {
        // no-op
      }
    }

    // Se não há código selecionado ou não encontrou correspondência, usa o índice atual
    // ou o primeiro registro disponível
    if (currentIndex < cafirDetails.length) {
      setSelectedCafirDetail(cafirDetails[currentIndex]);
    } else {
      setSelectedCafirDetail(cafirDetails[0]);
      setCurrentIndex(0);
    }
  }, [cafirDetails, selectedCafirCodes, currentIndex, setSelectedCafirCodes]);

  // Função para destacar geometria no mapa (se implementado)
  const _handleHighlight = () => {
    if (onHighlightGeometry && cafirData?.geojson) {
      onHighlightGeometry(cafirData);
    }
  };

  // Se não foi possível selecionar um detalhe do CAFIR, mostra mensagem alternativa
  if (!selectedCafirDetail) {
    return (
      <div className="p-4 bg-gray-50 rounded border border-gray-200">
        <div className="flex items-center text-gray-500">
          <FileText className="mr-2" size={18} />
          <p>Processando dados CAFIR...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 bg-gray-50 rounded-lg shadow-sm">
      {/* Cabeçalho */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center text-blue-700">
          <FileText className="mr-2" size={20} />
          <h3 className="text-lg font-medium">Cadastro Fiscal de Imóveis Rurais (CAFIR)</h3>
        </div>

        {/* Componente de navegação entre registros */}
        <RecordNavigator
          currentIndex={currentIndex}
          totalRecords={cafirDetails.length}
          onNavigate={handleNavigate}
        />
      </div>

      {/* Mostrar o detalhe CAFIR selecionado */}
      <CafirPropertyCard data={selectedCafirDetail} />
    </div>
  );
};

// Componente para exibir informações de um imóvel CAFIR individual
const CafirPropertyCard = ({ data }) => {
  // Função para formatar área
  const formatArea = (value) => {
    if (value === undefined || value === null || value === '') return '--';
    const num = parseFloat(value);
    return isNaN(num) ? '--' : num.toFixed(2) + ' ha';
  };

  // Função para formatar percentual
  const formatPercentual = (value) => {
    if (value === undefined || value === null || value === '') return '--';
    const num = parseFloat(value);
    return isNaN(num) ? '--' : num.toFixed(2) + '%';
  };

  // Função para formatar valor booleano sim/não
  const formatBoolean = (value) => {
    return value === true ? 'Sim' : value === false ? 'Não' : '--';
  };

  // Pega o nome da propriedade ou usa NIRF se o nome não estiver disponível
  const propertyName =
    formatValue(data.nome_da_propriedade) ||
    formatValue(data.nome) ||
    `Imóvel CAFIR ${formatValue(data.nirf || data.NIRF)}`;

  return (
    <div className="bg-white p-4 rounded-lg border border-blue-100 shadow-sm">
      <div className="flex items-center mb-3">
        <Building size={16} className="text-blue-600 mr-2" />
        <h4 className="text-md font-medium">{propertyName}</h4>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <DataItem label="NIRF" value={formatValue(data.nirf || data.NIRF)} />

        <DataItem
          label="Nome da Propriedade"
          value={formatValue(data.nome_da_propriedade || data.nome)}
        />

        <DataItem
          label="Área da Propriedade"
          value={formatArea(data.área_da_propriedade || data.area)}
        />

        <DataItem label="Logradouro" value={formatValue(data.logradouro)} />

        <DataItem label="Distrito" value={formatValue(data.distrito)} />

        <DataItem
          icon={<MapPin size={16} />}
          label="Município"
          value={formatValue(data.município || data.municipio)}
        />

        <DataItem label="UF" value={formatValue(data.uf)} />

        <DataItem label="CEP" value={formatValue(data.cep)} />

        <DataItem
          label="Data da Atualização"
          value={formatDateBr(data.data_da_atualização || data.data_atualizacao)}
        />

        <DataItem label="Imóvel Isento" value={formatBoolean(data.imóvel_isento)} />

        <DataItem label="Código SNCR" value={formatValue(data.código_sncr || data.codigo_sncr)} />
      </div>

      {/* Titulares do imóvel CAFIR */}
      {data.titulares && data.titulares.length > 0 && (
        <div className="mt-4 border-t pt-3">
          <h4 className="text-sm font-medium mb-3 flex items-center">
            <User size={16} className="text-gray-600 mr-2" />
            Titulares
          </h4>
          <div className="space-y-3">
            {data.titulares.map((titular, index) => (
              <div key={index} className="bg-gray-50 p-3 rounded border border-gray-100">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <DataItem label="Nome" value={formatValue(titular.nome)} />

                  <DataItem label="CPF/CNPJ" value={formatValue(titular.cpf_cnpj)} />

                  <DataItem label="Percentual" value={formatPercentual(titular.percentual)} />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default CafirTab;
